import { Box, Button, Card, CardContent, CircularProgress, Grid, TextField } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { Auth } from 'aws-amplify';
import { AuthPiece } from 'aws-amplify-react';
import { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';

import { useAuthStyles } from './styles';
import { useFormFields } from '../../../lib/hooks.lib';
import { ApplicationFormContainer } from '../../../private/pages/ApplicationsForm/applications-form.styles';

interface CustomConfirmSignUpProps {
  authState?: string;
  authData?: { username: string; phone: string };
  [key: string]: unknown;
}

export const CustomConfirmSignUp = (props: CustomConfirmSignUpProps): JSX.Element => {
  const classes = useAuthStyles();
  const authPiece = new AuthPiece(props);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [phone, setPhone] = useState('');
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [fields, handleFieldChange] = useFormFields({
    username: '',
    code: ''
  });

  useEffect(() => {
    if (props.authData) {
      handleFieldChange({ target: { id: 'username', value: props.authData.username } });
      setPhone(props.authData.phone);
    }
  }, [props.authData]);

  const getErrorMessage = (error: unknown): string => {
    return error instanceof Error ? error.message : String(error);
  };

  const handleConfirmPhone = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    try {
      await Auth.confirmSignUp(fields.username, fields.code);
      setLoading(false);
      authPiece.changeState('signIn');
      setErrorMessage(undefined);
    } catch (error) {
      console.log(error);
      setErrorMessage(getErrorMessage(error));
      setLoading(false);
    }
  };

  const checkFormFilled = () => {
    return Object.values(fields).every(Boolean) && fields.code.length === 6;
  };

  const formatPhone = (phoneNum: string) => {
    const phoneSub = phoneNum.substring(2);

    return `${phoneSub.slice(0, 3)}-${phoneSub.slice(3, 6)}-${phoneSub.slice(6)}`;
  };

  const handleResendDialogClickOpen = () => {
    setResendDialogOpen(true);
  };

  const handleResendDialogClickClose = () => {
    setResendDialogOpen(false);
  };

  const handleResendDialogClickSend = async (): Promise<void> => {
    setLoading(true);
    try {
      await Auth.resendSignUp(fields.username);
      setLoading(false);
      setResendDialogOpen(false);
    } catch (error) {
      console.log(error);
      setErrorMessage(getErrorMessage(error));
      setLoading(false);
      setResendDialogOpen(false);
    }
  };

  const renderConfirmSignUp = () => (
    <ApplicationFormContainer>
      <Box ml={4} mr={4} mb={3}>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => {
              setErrorMessage(undefined);
            }}
          >
            {errorMessage}
          </Alert>
        )}
        <Grid container justifyContent="center">
          <Grid item>
            <Typography variant="h4">Confirm Account</Typography>
            {!phone && (
              <Typography variant="body1">
                Enter the 6-digit code sent to your phone to confirm your new account and phone number. We will then ask you to sign in and
                enter another code as part of the normal log-in process.
              </Typography>
            )}
            {phone && (
              <div>
                <Typography variant="body1">
                  Enter the 6-digit code sent to <strong>{formatPhone(phone)}</strong> to confirm your new account and phone number. We will
                  then ask you to sign in and enter another code as part of the normal log-in process.
                </Typography>
              </div>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box pt={3} mb={3}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={10} sm={5}>
            <Card>
              <CardContent>
                <form noValidate>
                  <Grid container justifyContent="space-between" spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="username"
                        name="username"
                        label="Username"
                        type="text"
                        fullWidth
                        value={fields.username}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputMask mask="999999" disabled={false} maskChar={null} value={fields.code} onChange={handleFieldChange}>
                        {() => <TextField required id="code" name="code" label="Code" type="code" fullWidth autoFocus />}
                      </InputMask>
                    </Grid>
                  </Grid>
                  <Link component="button" type="button" onClick={handleResendDialogClickOpen} variant="body2" style={{ float: 'right' }}>
                    Resend code
                  </Link>
                  <Grid container justifyContent="center">
                    <Grid item style={{ marginTop: '20px' }}>
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        className={classes.button}
                        disabled={!checkFormFilled()}
                        onClick={(e) => handleConfirmPhone(e)}
                      >
                        Confirm Phone
                        {loading && (
                          <Box ml={2} mt={1}>
                            <CircularProgress size="0.8rem" color={'inherit'} />
                          </Box>
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      authPiece.changeState('signIn');
                      setErrorMessage(undefined);
                    }}
                    variant="body2"
                  >
                    Back to sign in
                  </Link>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={resendDialogOpen} onClose={handleResendDialogClickClose}>
        <DialogTitle>Resend Code</DialogTitle>
        <DialogContent>
          <DialogContentText>Enter your username. We will text the 6-digit code to your phone.</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="username"
            label="Username"
            type="text"
            fullWidth
            variant="standard"
            value={fields.username}
            onChange={handleFieldChange}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="contained" color="secondary" onClick={handleResendDialogClickClose}>
            Cancel
          </Button>
          <Button type="button" variant="contained" color="primary" onClick={handleResendDialogClickSend} disabled={!fields.username}>
            Resend
            {loading && (
              <Box ml={2} mt={1}>
                <CircularProgress size="0.8rem" color={'inherit'} />
              </Box>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </ApplicationFormContainer>
  );

  return <div>{props.authState === 'confirmSignUp' && renderConfirmSignUp()}</div>;
};
